"use client";

import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { signIn } from "@/lib/auth-client";
import Google from "./svg/google";
import Github from "./svg/github";

function LoginButton({
  provider,
  handleProviderSignIn,
  isLastUsed,
  children,
}: {
  provider: "google" | "github";
  handleProviderSignIn: (provider: "google" | "github") => void;
  isLastUsed?: boolean;
  children: React.ReactNode;
}) {
  const [pending, setPending] = useState(false);

  const handleClick = async () => {
    setPending(true);
    try {
      await handleProviderSignIn(provider);
    } finally {
      // Reset pending state regardless of success or failure,
      // as signIn might navigate away or throw an error handled by the parent.
      // If it doesn't navigate, we want the button to be active again.
      setPending(false);
    }
  };

  const className = pending ? "cursor-progress" : "hover:cursor-pointer";

  return (
    <div className="relative">
      {isLastUsed && (
        <span className="absolute -top-2 -right-2 z-10 rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
          Last used
        </span>
      )}
      <Button
        variant={"outline"}
        disabled={pending}
        onClick={handleClick}
        className={`${className} w-full ${isLastUsed ? "ring-2 ring-primary ring-offset-2" : ""}`}
      >
        {pending ? <Spinner /> : children}
      </Button>
    </div>
  );
}

type LoginFormProps = {
  redirectTo: string;
};

export function LoginForm({ redirectTo }: LoginFormProps) {
  const [lastProvider, setLastProvider] = useState<"google" | "github" | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedProvider = window.localStorage.getItem("last-login-provider");
    if (storedProvider === "google" || storedProvider === "github") {
      setLastProvider(storedProvider);
    }
  }, []);

  const handleProviderSignIn = async (provider: "google" | "github") => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("last-login-provider", provider);
      }
      setLastProvider(provider);
      const { error } = await signIn.social({
        provider,
        callbackURL: redirectTo,
        errorCallbackURL: "/error",
        newUserCallbackURL: redirectTo,
      });

      if (error) {
        toast.error(error.message || "Sign in failed");
      }
    } catch {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <>
      <LoginButton
        provider="google"
        handleProviderSignIn={handleProviderSignIn}
        isLastUsed={lastProvider === "google"}
      >
        <Google />
        Login with Google
      </LoginButton>
      <LoginButton
        provider="github"
        handleProviderSignIn={handleProviderSignIn}
        isLastUsed={lastProvider === "github"}
      >
        <Github />
        Login with Github
      </LoginButton>
    </>
  );
}
